// ControlAssessmentsPage — control assessments recorded in a configurable look-back period.
// Shows summary cards (total, by effectiveness rating), multi-select filters, and a table.

import { useState, useMemo, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { reportingApi } from '@/lib/api/reporting'
import type { AssessmentReportRow } from '@/lib/api/reporting'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Shield, Calendar, ChevronDown, X } from 'lucide-react'

const PERIOD_OPTIONS = [7, 14, 30, 60, 90] as const

export function ControlAssessmentsPage() {
  const { slug } = useTenant()
  const api = useApi()
  const rptApi = reportingApi(api)
  const navigate = useNavigate()
  const [days, setDays] = useState<number>(30)

  // Client-side filters
  const [controlSearch, setControlSearch] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set())
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set())
  const [selectedRatings, setSelectedRatings] = useState<Set<string>>(new Set())
  const [selectedOwners, setSelectedOwners] = useState<Set<string>>(new Set())

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: queryKeys.reporting.controlAssessments(slug, { days }),
    queryFn: () => rptApi.controlAssessmentsLastPeriod(days),
  })

  // Derive unique filter options from the full dataset
  const filterOptions = useMemo(() => {
    if (!data) return { categories: [], types: [], ratings: [], owners: [] }
    const categories = new Set<string>()
    const types = new Set<string>()
    const ratings = new Set<string>()
    const owners = new Set<string>()
    for (const a of data.assessments) {
      if (a.category_name) categories.add(a.category_name)
      types.add(a.assessment_type)
      ratings.add(a.rating)
      if (a.assessed_by_name) owners.add(a.assessed_by_name)
    }
    return {
      categories: [...categories].sort(),
      types: [...types].sort(),
      ratings: [...ratings].sort(),
      owners: [...owners].sort(),
    }
  }, [data])

  // Apply client-side filters
  const filtered = useMemo(() => {
    if (!data) return []
    return data.assessments.filter((a) => {
      if (controlSearch && !a.control_name.toLowerCase().includes(controlSearch.toLowerCase())) return false
      if (selectedCategories.size > 0 && !selectedCategories.has(a.category_name)) return false
      if (selectedTypes.size > 0 && !selectedTypes.has(a.assessment_type)) return false
      if (selectedRatings.size > 0 && !selectedRatings.has(a.rating)) return false
      if (selectedOwners.size > 0 && !selectedOwners.has(a.assessed_by_name)) return false
      return true
    })
  }, [data, controlSearch, selectedCategories, selectedTypes, selectedRatings, selectedOwners])

  const hasActiveFilters = controlSearch !== '' || selectedCategories.size > 0 ||
    selectedTypes.size > 0 || selectedRatings.size > 0 || selectedOwners.size > 0

  function clearAllFilters() {
    setControlSearch('')
    setSelectedCategories(new Set())
    setSelectedTypes(new Set())
    setSelectedRatings(new Set())
    setSelectedOwners(new Set())
  }

  if (isLoading) return <TableSkeleton rows={8} />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load assessment activity report"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  if (!data) return null

  return (
    <div className="space-y-4 overflow-y-auto p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Assessment Activity</h1>
          <p className="text-sm text-muted-foreground">
            Control assessments recorded in the last {data.period_days} days, filtered by your
            permissions.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <label htmlFor="period-days" className="text-sm text-muted-foreground">
            Period:
          </label>
          <select
            id="period-days"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-md border bg-background px-2 py-1 text-sm"
          >
            {PERIOD_OPTIONS.map((n) => (
              <option key={n} value={n}>
                Last {n} days
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <SummaryCard label="Total Assessments" value={data.total_assessments} />
        {data.by_effectiveness.map((e) => (
          <SummaryCard
            key={e.rating}
            label={formatRating(e.rating)}
            value={e.count}
            color={ratingColor(e.rating)}
          />
        ))}
      </div>

      {/* Assessments table */}
      {data.assessments.length === 0 ? (
        <div className="flex h-48 items-center justify-center text-muted-foreground">
          <div className="space-y-2 text-center">
            <Shield className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm font-medium">No assessments in this period</p>
            <p className="text-xs">
              No control assessments were recorded in the last {data.period_days} days that you have
              permission to view.
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <table className="w-full table-fixed text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="w-[200px] px-3 py-2 text-left font-medium">Control</th>
                <th className="w-[140px] px-3 py-2 text-left font-medium">Category</th>
                <th className="w-[160px] px-3 py-2 text-left font-medium">Type</th>
                <th className="w-[120px] px-3 py-2 text-left font-medium">Rating</th>
                <th className="w-[130px] px-3 py-2 text-left font-medium">Assessed By</th>
                <th className="w-[90px] px-3 py-2 text-left font-medium">Date</th>
                <th className="px-3 py-2 text-left font-medium">
                  <div className="flex items-center justify-between">
                    <span>Notes</span>
                    {hasActiveFilters && (
                      <span className="flex items-center gap-2 font-normal">
                        <span className="text-xs text-muted-foreground">
                          {filtered.length} of {data.assessments.length}
                        </span>
                        <button
                          onClick={clearAllFilters}
                          className="inline-flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-3 w-3" />
                          Clear
                        </button>
                      </span>
                    )}
                  </div>
                </th>
              </tr>
              {/* Filter row aligned to columns */}
              <tr className="border-b bg-muted/20">
                <th className="px-2 py-1.5 text-left font-normal">
                  <input
                    type="text"
                    placeholder="Filter…"
                    value={controlSearch}
                    onChange={(e) => setControlSearch(e.target.value)}
                    className="w-full rounded border bg-background px-2 py-1 text-sm font-normal placeholder:text-muted-foreground/50"
                  />
                </th>
                <th className="px-2 py-1.5 text-left font-normal">
                  <MultiSelectDropdown
                    label="All"
                    options={filterOptions.categories}
                    selected={selectedCategories}
                    onChange={setSelectedCategories}
                    fullWidth
                  />
                </th>
                <th className="px-2 py-1.5 text-left font-normal">
                  <MultiSelectDropdown
                    label="All"
                    options={filterOptions.types}
                    selected={selectedTypes}
                    onChange={setSelectedTypes}
                    formatOption={formatAssessmentType}
                    fullWidth
                  />
                </th>
                <th className="px-2 py-1.5 text-left font-normal">
                  <MultiSelectDropdown
                    label="All"
                    options={filterOptions.ratings}
                    selected={selectedRatings}
                    onChange={setSelectedRatings}
                    formatOption={formatRating}
                    fullWidth
                  />
                </th>
                <th className="px-2 py-1.5 text-left font-normal">
                  <MultiSelectDropdown
                    label="All"
                    options={filterOptions.owners}
                    selected={selectedOwners}
                    onChange={setSelectedOwners}
                    fullWidth
                  />
                </th>
                <th className="px-2 py-1.5" />
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-muted-foreground">
                    <p className="text-sm">No assessments match the current filters</p>
                    <button onClick={clearAllFilters} className="mt-1 text-xs text-primary hover:underline">
                      Clear all filters
                    </button>
                  </td>
                </tr>
              ) : (
                filtered.map((a, idx) => (
                  <AssessmentRow
                    key={`${a.control_id}-${a.assessment_type}-${idx}`}
                    assessment={a}
                    onClick={() => navigate({ to: `/t/${slug}/controls/${a.control_id}` })}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// --- Multi-select dropdown ---

interface MultiSelectDropdownProps {
  label: string
  options: string[]
  selected: Set<string>
  onChange: (next: Set<string>) => void
  formatOption?: (value: string) => string
  /** When true the trigger button stretches to fill its container. */
  fullWidth?: boolean
}

function MultiSelectDropdown({ label, options, selected, onChange, formatOption, fullWidth }: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  function toggle(value: string) {
    const next = new Set(selected)
    if (next.has(value)) next.delete(value)
    else next.add(value)
    onChange(next)
  }

  const fmt = formatOption ?? ((v: string) => v)
  const buttonLabel = selected.size === 0
    ? label
    : selected.size === 1
      ? fmt([...selected][0]!)
      : `${label} (${selected.size})`

  if (options.length === 0) return null

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-sm ${
          fullWidth ? 'w-full justify-between' : ''
        } ${
          selected.size > 0 ? 'border-primary/40 bg-primary/5 font-medium' : 'bg-background'
        }`}
      >
        <span className="min-w-0 truncate">{buttonLabel}</span>
        <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 max-h-56 w-52 overflow-y-auto rounded-md border bg-background shadow-lg">
          {options.map((opt) => (
            <label
              key={opt}
              className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted/50"
            >
              <input
                type="checkbox"
                checked={selected.has(opt)}
                onChange={() => toggle(opt)}
                className="h-3.5 w-3.5 rounded border-gray-300"
              />
              <span className="truncate">{fmt(opt)}</span>
            </label>
          ))}
          {selected.size > 0 && (
            <button
              onClick={() => onChange(new Set())}
              className="w-full border-t px-3 py-1.5 text-left text-xs text-muted-foreground hover:bg-muted/50"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// --- Sub-components ---

function SummaryCard({
  label,
  value,
  color,
}: {
  label: string
  value: number
  color?: string
}) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-2xl font-bold tabular-nums ${color ?? ''}`}>{value}</p>
    </div>
  )
}

function AssessmentRow({
  assessment,
  onClick,
}: {
  assessment: AssessmentReportRow
  onClick: () => void
}) {
  return (
    <tr className="cursor-pointer border-b align-top last:border-0 hover:bg-muted/30" onClick={onClick}>
      <td className="truncate px-3 py-2 font-medium" title={assessment.control_name}>
        {assessment.control_name}
      </td>
      <td className="truncate px-3 py-2 text-muted-foreground">{assessment.category_name || '—'}</td>
      <td className="px-3 py-2">{formatAssessmentType(assessment.assessment_type)}</td>
      <td className="px-3 py-2">
        <RatingBadge rating={assessment.rating} />
      </td>
      <td className="truncate px-3 py-2 text-muted-foreground">
        {assessment.assessed_by_name || '—'}
      </td>
      <td className="whitespace-nowrap px-3 py-2 tabular-nums">
        {assessment.assessed_at
          ? new Date(assessment.assessed_at).toLocaleDateString()
          : '—'}
      </td>
      <td className="whitespace-pre-wrap break-words px-3 py-2 text-muted-foreground">
        {assessment.notes || '—'}
      </td>
    </tr>
  )
}

function RatingBadge({ rating }: { rating: string }) {
  const styles: Record<string, string> = {
    effective: 'bg-green-100 text-green-700',
    partially_effective: 'bg-amber-100 text-amber-700',
    ineffective: 'bg-red-100 text-red-700',
    not_assessed: 'bg-gray-100 text-gray-700',
  }
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${styles[rating] ?? 'bg-gray-100 text-gray-700'}`}
    >
      {formatRating(rating)}
    </span>
  )
}

function formatRating(rating: string): string {
  return rating
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function formatAssessmentType(t: string): string {
  return t
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function ratingColor(rating: string): string {
  const colors: Record<string, string> = {
    effective: 'text-green-600',
    partially_effective: 'text-amber-600',
    ineffective: 'text-red-600',
  }
  return colors[rating] ?? ''
}
